import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  CompanyIdentifier,
  getCustomLabels,
  createCustomLabel,
  deleteCustomLabel,
  updateCustomLabel,
  getGroupLabelTree,
  addGroupSpecificLabel,
  moveGroupParentCategory,
  deleteGroupSpecificLabel,
  toggleCustomLabel,
  reorderCustomLabel,
} from '~apis/custom-label';
import { TransactionsQueryKey } from '~hooks/queries/transactions/query';

import { AnalysisQueryKey } from '../analysis/query';
import { RuleQueryKey } from '../rule/query';
import { CASH_FLOW_KEY } from '../useGetCashFlow';

export const CustomLabelsQueryKey = {
  default: () => ['transactionsCustomLabels'],
  list: ({ companyId }: CompanyIdentifier) => [
    ...CustomLabelsQueryKey.default(),
    'list',
    companyId,
  ],
  tree: ({ companyId }: CompanyIdentifier) => [
    ...CustomLabelsQueryKey.default(),
    'tree',
    companyId,
  ],
};

export function useGroupLabels(identifier: CompanyIdentifier) {
  return useQuery({
    queryKey: CustomLabelsQueryKey.list(identifier),
    queryFn: () => getCustomLabels(identifier),
    enabled: !!identifier.companyId,
    staleTime: 0,
  });
}

export function useGroupLabelsTree(identifier: CompanyIdentifier) {
  return useQuery({
    queryKey: CustomLabelsQueryKey.tree(identifier),
    queryFn: () => getGroupLabelTree(identifier),
    enabled: !!identifier.companyId,
    refetchOnMount: true,
  });
}

export function useAddGroupSpecificLabel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: addGroupSpecificLabel,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries(TransactionsQueryKey.default());
      queryClient.invalidateQueries(CustomLabelsQueryKey.list(variables));
      queryClient.invalidateQueries(CustomLabelsQueryKey.tree(variables));
      queryClient.invalidateQueries(AnalysisQueryKey.default());
    },
  });
}

export function useMoveGroupParentCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: moveGroupParentCategory,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries(TransactionsQueryKey.default());
      queryClient.invalidateQueries(CustomLabelsQueryKey.list(variables));
      queryClient.invalidateQueries(CustomLabelsQueryKey.tree(variables));
    },
  });
}

export function useDeleteGroupSpecificLabel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteGroupSpecificLabel,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries(TransactionsQueryKey.default());
      queryClient.invalidateQueries(CustomLabelsQueryKey.list(variables));
      queryClient.invalidateQueries(CustomLabelsQueryKey.tree(variables));
    },
  });
}

export function useCreateCustomLabel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createCustomLabel,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries(TransactionsQueryKey.default());
      queryClient.invalidateQueries(CustomLabelsQueryKey.list(variables));
      queryClient.invalidateQueries(CASH_FLOW_KEY.default);
    },
  });
}

export function useUpdateCustomLabel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateCustomLabel,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries(CustomLabelsQueryKey.list(variables));
      queryClient.invalidateQueries(CustomLabelsQueryKey.tree(variables));
      queryClient.invalidateQueries(TransactionsQueryKey.default());
      queryClient.invalidateQueries(AnalysisQueryKey.default());
    },
  });
}
export function useToggleCustomLabel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: toggleCustomLabel,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries(CustomLabelsQueryKey.list(variables));
      queryClient.invalidateQueries(CustomLabelsQueryKey.tree(variables));
      queryClient.invalidateQueries(TransactionsQueryKey.default());
    },
  });
}

export function useReorderCustomLabels() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: reorderCustomLabel,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries(TransactionsQueryKey.default());
      queryClient.invalidateQueries(CustomLabelsQueryKey.list(variables));
      queryClient.invalidateQueries(CustomLabelsQueryKey.tree(variables));
    },
  });
}

export function useDeleteCustomLabel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteCustomLabel,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries(CustomLabelsQueryKey.list(variables));
      queryClient.invalidateQueries(TransactionsQueryKey.default());
      queryClient.invalidateQueries(RuleQueryKey.default(variables.companyId));
    },
  });
}
